/**
 * ModulePinGate — wrapper, który chroni dany moduł PIN-em.
 * Po wejściu w moduł sprawdza status PIN-a; jeśli ustawiony, blokuje UI
 * dopóki user nie wpisze poprawnego PIN-u. Dostęp utrzymywany w sessionStorage
 * (klucz `mpin:<module>`), aby po jednorazowym odblokowaniu nie pytał ponownie
 * podczas tej samej sesji przeglądarki.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';
import PortalOverlay from './common/PortalOverlay';

const accent = '#06b6d4';

const PinModal = ({ title, onSubmit, onCancel, error, busy }) => {
  const [pin, setPin] = useState('');
  const submit = (e) => {
    e?.preventDefault?.();
    if (pin.length >= 4) onSubmit(pin);
  };
  return (
    <PortalOverlay onClose={onCancel}>
    <div
      data-testid="module-pin-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(2,6,23,0.92)', backdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          background: 'linear-gradient(180deg, rgba(17,24,39,0.97), rgba(2,6,23,0.97))',
          borderRadius: 20, padding: 28, maxWidth: 420, width: '100%',
          border: `1px solid ${accent}33`,
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          color: '#fff', textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 6 }}>🔒</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800 }}>{title}</h2>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#94a3b8' }}>
          Ten moduł zawiera dane wrażliwe. Podaj PIN aby kontynuować.
        </p>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus
          maxLength={8}
          value={pin}
          onChange={(e) => setPin((e.target.value || '').replace(/\D/g, '').slice(0, 8))}
          data-testid="module-pin-input"
          placeholder="• • • •"
          style={{
            width: '220px', padding: '14px 18px',
            fontSize: 28, letterSpacing: '0.5em', textAlign: 'center',
            borderRadius: 12, marginBottom: 12,
            background: 'rgba(255,255,255,0.05)',
            border: `1px solid rgba(255,255,255,0.15)`,
            color: '#fff', outline: 'none',
          }}
        />
        {error && (
          <div data-testid="module-pin-error" style={{ color: '#ef4444', fontSize: 13, marginBottom: 8, fontWeight: 600 }}>
            ⚠️ {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14 }}>
          <button
            type="button"
            onClick={onCancel}
            data-testid="module-pin-cancel"
            style={{
              padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700,
              background: 'rgba(255,255,255,0.05)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer',
            }}
          >Anuluj</button>
          <button
            type="submit"
            disabled={busy || pin.length < 4}
            data-testid="module-pin-submit"
            style={{
              padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 800,
              background: `linear-gradient(135deg, ${accent}, #6366f1)`,
              color: '#fff', border: 'none',
              cursor: busy || pin.length < 4 ? 'not-allowed' : 'pointer',
              opacity: busy || pin.length < 4 ? 0.6 : 1,
              boxShadow: `0 8px 20px ${accent}55`,
            }}
          >
            {busy ? '⏳ Sprawdzam…' : '🔓 Odblokuj'}
          </button>
        </div>
      </form>
    </div>
    </PortalOverlay>
  );
};

const ModulePinGate = ({ moduleId, location, title, onCancel, children }) => {
  const sessionKey = `mpin:${moduleId}:${location}`;
  const [phase, setPhase] = useState('loading'); // loading | locked | unlocked
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/module-access`, { params: { location, module: moduleId } });
      if (!r.data?.isSet) {
        // Brak PIN-u — pełen dostęp
        setPhase('unlocked');
        return;
      }
      // PIN ustawiony — sprawdź czy już odblokowany w tej sesji
      if (sessionStorage.getItem(sessionKey) === '1') {
        setPhase('unlocked');
      } else {
        setPhase('locked');
      }
    } catch (e) {
      // Fail-open: jeśli API padło, wpuść (lepiej niż blokować dane managera)
      setPhase('unlocked');
    }
  }, [location, moduleId, sessionKey]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const handleSubmit = async (pin) => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/module-access/verify`, { value: pin }, { params: { location, module: moduleId } });
      if (r.data?.valid) {
        sessionStorage.setItem(sessionKey, '1');
        setPhase('unlocked');
      } else {
        setError('Niepoprawny PIN');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Błąd serwera');
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'loading') {
    return <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>⏳ Sprawdzanie dostępu…</div>;
  }
  if (phase === 'locked') {
    return (
      <PinModal
        title={title || '🔒 Moduł chroniony PIN-em'}
        error={error}
        busy={busy}
        onSubmit={handleSubmit}
        onCancel={onCancel || (() => window.history.back())}
      />
    );
  }
  return children;
};

export default ModulePinGate;
